import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { ChatNotificationsProvider } from "./contexts/ChatNotificationsContext";
import Auth from "./Auth";
import Home from "./pages/Home";
import Events from "./pages/Events";
import EventDetails from "./pages/EventDetails";
import Profile from "./pages/Profile";
import ProtectedRoute from "./ProtectedRoute";
import Buddies from "./pages/Buddies";
import ChatPage from "./pages/ChatPage";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Careers from "./pages/Careers";
import Cities from "./pages/Cities";
import Artists from "./pages/Artists";
import Organizers from "./pages/Organizers";
import Venues from "./pages/Venues";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Cookies from "./pages/Cookies";
import NotFound from "./pages/NotFound";

// Public marketing/legal pages stay visible to signed-out visitors (e.g. anyone
// clicking a footer link) instead of being bounced to /auth like the core app routes.
const PUBLIC_PATHS = [
  "/auth",
  "/about",
  "/contact",
  "/careers",
  "/cities",
  "/artists",
  "/organizers",
  "/venues",
  "/privacy",
  "/terms",
  "/cookies",
];

// Centralized scroll-restoration: a normal navigation to a new route (e.g.
// from the hamburger menu, or any Link/navigate() call) should land at the
// top of the new page, matching native browser navigation expectations.
// Navigations that intentionally restore a previous scroll position (the
// Events page reopened via its own "Back to Events" flow, see requirement
// 11 / Events.js's `partybuddy.events.snapshot` restoration) mark their
// history entry with `state.preserveScroll` so this effect skips them —
// this is the one shared place scroll behavior is decided, rather than
// scattering window.scrollTo() calls across every navigation callsite.
function ScrollToTop() {
  const location = useLocation();
  // Disable the browser's own automatic scroll restoration on back/forward
  // navigation once, up front — otherwise it can race with (and randomly
  // win or lose against) our own explicit restore logic below and in
  // Events.js, producing inconsistent jumps. With this off, scroll position
  // is ALWAYS decided by our own JS, deterministically.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
    return undefined;
  }, []);
  useEffect(() => {
    if (location.state?.preserveScroll) return;
    window.scrollTo(0, 0);
  }, [location.pathname, location.state]);
  return null;
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();

  // Chat-style pages (ChatPage and Buddies, which embeds an inline chat
  // panel) size themselves to exactly "viewport minus navbar" so only the
  // message history scrolls and the composer never needs a page-level
  // scroll to reach (requirement: contained chat viewport). The global
  // Footer below would silently defeat that by adding extra page height
  // regardless — so it's intentionally omitted on exactly those routes.
  const hideFooter = location.pathname === "/buddies" || location.pathname.startsWith("/chat/");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user && !PUBLIC_PATHS.includes(location.pathname)) {
        // Preserve the originally requested URL (e.g. a shared event link)
        // via router state so Auth.js can send the user back after login.
        navigate("/auth", { replace: true, state: { from: location } });
      }
    });
    return () => unsub();
  }, [navigate, location]);

  return (
    <ChatNotificationsProvider>
      <ScrollToTop />
      <Navbar />
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Events />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/event/:eventId"
          element={
            <ProtectedRoute>
              <EventDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/buddies"
          element={
            <ProtectedRoute>
              <Buddies />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:buddyId"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        /> {/* ✅ Chat-Route */}
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/cities" element={<Cities />} />
        <Route path="/artists" element={<Artists />} />
        <Route path="/organizers" element={<Organizers />} />
        <Route path="/venues" element={<Venues />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!hideFooter && <Footer />}
    </ChatNotificationsProvider>
  );
}

export default AppRoutes;